import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, shareReplay } from 'rxjs';

import { CapicheLoggerService } from 'capiche-logger';
import { AuthService, UserService } from 'capiche-auth';

import { StripeApiService } from './stripe-api.service';
import { CAPICHE_SUBSCRIPTION_CONFIG } from '../capiche-subscription.config';

@Injectable({
  providedIn: 'root',
})
export class CapicheSubscriptionService {
  private readonly FREE_MODE: boolean = true; // TEMPORARY OVERRIDE - REMOVE/false THIS LINE TO ENABLE SUBSCRIPTION CHECKS
  private readonly alwaysSubscribed$ = new BehaviorSubject<boolean>(true).asObservable();

  config = inject(CAPICHE_SUBSCRIPTION_CONFIG);
  private stripeApiService = inject(StripeApiService);
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private logger = new CapicheLoggerService('capiche-subscription.service:', false);

  private readonly LOCAL_KEY = 'mockSubscriptionState';
  private readonly useMock = false; //!isCapicheProduction();

  private checkoutInUrl = new BehaviorSubject<string | null>(null); // populated when the checkout flow starts, null when not in checkout flow
  checkoutUrl$ = this.checkoutInUrl.asObservable();

  private mockSubscriptionState = new BehaviorSubject<boolean | null>(
    localStorage.getItem(this.LOCAL_KEY) === 'true' ? true : false,
  );

  private realSubscriptionState$ = new BehaviorSubject<boolean | null>(null);

  hasActiveSubscription$: Observable<boolean | null> = (this.FREE_MODE
    ? this.alwaysSubscribed$ // always true in free mode
    : this.useMock
      ? this.mockSubscriptionState.asObservable()
      : this.realSubscriptionState$
  ).pipe(distinctUntilChanged(), shareReplay(1));

  constructor() {
    this.logger.log(
      'constructor:',
      this.FREE_MODE ? 'free' : 'paid',
      this.useMock ? 'mock' : 'real',
      this.config.envName,
      this.config.baseStripeApiUrl,
      this.config.stripePriceId,
    );

    if (!this.useMock) {
      this.userService.currentUser$.pipe(distinctUntilChanged()).subscribe((user) => {
        this.logger.log('user changed:', user);
        if (!user) {
          this.realSubscriptionState$.next(null); // there is no user so we don't know the subscription status
          return;
        }
        this.fetchSubscriptionStatus();
      });
    }

    this.hasActiveSubscription$.subscribe((state) => {
      this.logger.log('hasActiveSubscription$ state change:', state);
    });
  }

  async fetchSubscriptionStatus() {
    if (this.FREE_MODE) {
      this.logger.log('fetchSubscriptionStatus: FREE_MODE → always true');
      this.realSubscriptionState$.next(true);
      return;
    }

    // CSW-1662: anyone with a school membership — student or staff — is
    // covered by the school's arrangement, not a personal Stripe sub. (A
    // student accesses via their teacher; a teacher gets try-before-you-buy,
    // CSW-1379.) Check the token before hitting Stripe.
    if (this.hasSchoolMembership()) {
      this.logger.log('fetchSubscriptionStatus: school member → always true');
      this.realSubscriptionState$.next(true);
      return;
    }

    this.logger.log('fetchSubscriptionStatus() → calling StripeApiService');
    try {
      const status = await this.stripeApiService.getSubscriptionStatus();
      this.logger.log('Fetched real subscription status:', status);
      this.realSubscriptionState$.next(status);
    } catch (error) {
      this.logger.error('Failed to fetch subscription status:', error);
      this.realSubscriptionState$.next(false);
    }
  }

  /** True if the signed-in user's token carries any school membership
   *  (CSW-1662 `memberships` claim) — student or staff. */
  private hasSchoolMembership(): boolean {
    try {
      const idToken = this.authService.getIdToken();
      if (!idToken) return false;
      // JWT payload is the second segment, base64url encoded.
      const payload = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      const memberships = (payload['memberships'] ?? {}) as Record<string, unknown>;
      return Object.keys(memberships).length > 0;
    } catch {
      return false;
    }
  }

  async refreshSubscriptionStatus(): Promise<void> {
    await this.fetchSubscriptionStatus();
  }

  /** Mock Subscribe */
  mockSubscribe() {
    if (!this.useMock) return;
    this.logger.log('mockSubscribe()');
    localStorage.setItem(this.LOCAL_KEY, 'true');
    this.mockSubscriptionState.next(true);
  }

  /** Mock Unsubscribe */
  mockUnsubscribe() {
    if (!this.useMock) return;
    this.logger.log('mockUnsubscribe()');
    localStorage.setItem(this.LOCAL_KEY, 'false');
    this.mockSubscriptionState.next(false);
  }

  beginCheckoutFlow(url: string | null) {
    this.logger.log('beginCheckoutFlow:', this.userService.currentUser$, url);
    this.checkoutInUrl.next(url); // checkout process starts
  }

  endCheckoutFlow() {
    this.logger.log('endCheckoutFlow:', this.userService.currentUser$);
    this.checkoutInUrl.next(null); // checkout process ends
  }
}
